/**
 * The browser's microphone, as the live sockets and the transcriber want it:
 * PCM16 little-endian mono at `LIVE_INPUT_RATE`, a tenth of a second a frame.
 *
 * The phone's `AudioRecorder` hands over Float32 frames; here `getUserMedia`
 * gives a stream, an `AudioWorklet` (`capture-worklet.ts`) lifts its samples off
 * the audio thread, and `pcm-stream.ts` brings them to the wire's rate and cuts
 * them into frames, encoded by the phone's own `floatToPcm16`.
 *
 * **The rate.** A context opened at 16 kHz lets the browser resample the
 * microphone itself, with its own filter, and Chromium does. A browser that
 * refuses to join a stream to a context at another rate throws on
 * `createMediaStreamSource`; the context is then opened at the device's rate and
 * `pcm-stream.ts` does the resampling. Either way the frames leave at 16 kHz.
 *
 * **Echo.** The capture asks for the browser's echo cancellation, which hears
 * what the page itself plays, so the model's voice from the speaker is not sent
 * back as the person's. The hooks keep the phone's half duplex on top of it.
 *
 * **Live mode's ear** (docs/plans/os-voice-conversation.md, step 4.4): the
 * track's own `echoCancellation` is read back (`echoCancel`), since a browser
 * may refuse the constraint, and full duplex is declared only when it is on.
 * A live call may ask for shorter frames (`frameSamples`, 50 ms for the
 * duck), and hears the track ending, being muted (another app took the
 * microphone) and unmuted, and the context's state (Safari's `interrupted`).
 *
 * **Refusals** are told apart, as the phone tells them apart: a person who said
 * no (or a browser policy that did) is `mic_off`, and the voice surfaces draw
 * the microphone-off state; no microphone at all is `no_mic`; anything else is
 * `failed`.
 */
import { LIVE_INPUT_RATE } from '@alluzo/shared';

import { floatToPcm16 } from '@/lib/live-pcm';

import { CAPTURE_BATCH, CAPTURE_PROCESSOR, captureWorkletUrl } from './capture-worklet';
import { createPcmFramer } from './pcm-stream';

/** Samples a frame: a tenth of a second at the input rate, as the phone's recorder is asked for. */
export const FRAME_SAMPLES = LIVE_INPUT_RATE / 10;

export type MicFailure = 'mic_off' | 'no_mic' | 'failed';

export class MicrophoneError extends Error {
  constructor(
    readonly code: MicFailure,
    message: string,
  ) {
    super(message);
    this.name = 'MicrophoneError';
  }
}

/** Which of the three a thrown thing is: a refusal, no device, or anything else. */
export const micFailureOf = (error: unknown): MicFailure => {
  if (error instanceof MicrophoneError) return error.code;
  const name = error instanceof Error || error instanceof DOMException ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') return 'mic_off';
  if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'DevicesNotFoundError') return 'no_mic';
  return 'failed';
};

export type Microphone = {
  /** The rate the browser captures at. The frames are at `LIVE_INPUT_RATE` whatever it is. */
  readonly captureRate: number;
  /** The track reads echo cancellation back on: the browser cancels what the page plays. */
  readonly echoCancel: boolean;
  /** Wake the capture context after an interruption, from the person's gesture. */
  readonly resume: () => Promise<void>;
  /**
   * Stop capturing and give the device back. `flush` hands over the samples
   * still waiting for the rest of their frame as one short frame first, which
   * dictation wants (the last word) and a live call does not. Idempotent.
   */
  readonly stop: (options?: { readonly flush?: boolean }) => Promise<void>;
};

const CAPTURE: MediaTrackConstraints = {
  channelCount: 1,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

type Opened = { readonly context: AudioContext; readonly source: MediaStreamAudioSourceNode };

const openContext = (stream: MediaStream): Opened => {
  try {
    const context = new AudioContext({ sampleRate: LIVE_INPUT_RATE, latencyHint: 'interactive' });
    try {
      return { context, source: context.createMediaStreamSource(stream) };
    } catch {
      void context.close().catch(() => undefined);
    }
  } catch {
    /* A browser that cannot open a context at 16 kHz opens one at the device's rate. */
  }
  const context = new AudioContext({ latencyHint: 'interactive' });
  return { context, source: context.createMediaStreamSource(stream) };
};

const messageOf = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message !== '' ? error.message : fallback;

export type MicrophoneOptions = {
  /** Samples a frame, at the input rate: `FRAME_SAMPLES` (100 ms) unless asked. */
  readonly frameSamples?: number;
  /** The track ended (the device went), was muted (another app took it), or unmuted. */
  readonly onTrack?: (what: 'ended' | 'muted' | 'unmuted') => void;
  /** The capture context's state changed: `running`, `suspended`, `closed`, Safari's `interrupted`. */
  readonly onContextState?: (state: string) => void;
};

/**
 * Ask for the microphone and start handing frames over. Rejects with a
 * `MicrophoneError` when the person refuses, there is no microphone, or the
 * capture will not start.
 */
export const openMicrophone = async (onFrame: (pcm: Uint8Array<ArrayBuffer>) => void, options: MicrophoneOptions = {}): Promise<Microphone> => {
  const devices = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
  if (devices === undefined || typeof devices.getUserMedia !== 'function') {
    throw new MicrophoneError('failed', 'This browser cannot reach a microphone.');
  }

  let stream: MediaStream;
  try {
    stream = await devices.getUserMedia({ audio: CAPTURE });
  } catch (error) {
    throw new MicrophoneError(micFailureOf(error), messageOf(error, 'The microphone could not start.'));
  }

  let context: AudioContext | null = null;
  try {
    const opened = openContext(stream);
    context = opened.context;
    if (context.state === 'suspended') await context.resume();
    await context.audioWorklet.addModule(captureWorkletUrl());
    const node = new AudioWorkletNode(context, CAPTURE_PROCESSOR, {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      channelCount: 1,
      channelCountMode: 'explicit',
      channelInterpretation: 'speakers',
      processorOptions: { batch: CAPTURE_BATCH },
    });
    const framer = createPcmFramer({
      fromRate: context.sampleRate,
      toRate: LIVE_INPUT_RATE,
      frameSamples: options.frameSamples ?? FRAME_SAMPLES,
      encode: floatToPcm16,
      onFrame,
    });
    node.port.onmessage = (event: MessageEvent<unknown>) => {
      if (event.data instanceof Float32Array) framer.push(event.data);
    };
    opened.source.connect(node);
    // The processor writes nothing, so this is silence; it is connected because
    // a node nothing pulls on is a node a browser may never run.
    node.connect(context.destination);

    const held = context;
    const track = stream.getAudioTracks()[0] ?? null;
    // Read back, not assumed: a browser may refuse the constraint and say so only here.
    const echoCancel = track?.getSettings().echoCancellation === true;
    if (track !== null) {
      track.onended = () => options.onTrack?.('ended');
      track.onmute = () => options.onTrack?.('muted');
      track.onunmute = () => options.onTrack?.('unmuted');
    }
    held.onstatechange = () => options.onContextState?.(held.state);
    let stopped = false;
    const stop = async (stopping?: { readonly flush?: boolean }): Promise<void> => {
      if (stopped) return;
      stopped = true;
      node.port.onmessage = null;
      held.onstatechange = null;
      if (track !== null) {
        track.onended = null;
        track.onmute = null;
        track.onunmute = null;
      }
      if (stopping?.flush === true) framer.flush();
      try {
        opened.source.disconnect();
        node.disconnect();
      } catch {
        /* Already apart. */
      }
      for (const track of stream.getTracks()) track.stop();
      try {
        await held.close();
      } catch {
        /* Closed either way. */
      }
    };
    const resume = async (): Promise<void> => {
      if (!stopped && held.state !== 'running' && held.state !== 'closed') await held.resume();
    };
    return { captureRate: context.sampleRate, echoCancel, resume, stop };
  } catch (error) {
    for (const track of stream.getTracks()) track.stop();
    if (context !== null) void context.close().catch(() => undefined);
    throw new MicrophoneError('failed', messageOf(error, 'The microphone could not start.'));
  }
};
